import { type LanguageCode, type SignupSource } from '@heidi/schema/ml-scribe';
import { type CountryType } from '@heidi/utils/countries';
import { getCountry } from '@heidi/utils/get-country';
import { atomWithHash } from '@heidi/utils/jotai-helpers';
import { ONBOARDING_PAYMENT_CHOSEN, type ClinicianTypeInput } from '../../contracts';

export const paymentChosenAtom = atomWithHash<ONBOARDING_PAYMENT_CHOSEN>(
  'paymentChosen',
  ONBOARDING_PAYMENT_CHOSEN.FREE
);

export const firstNameAtom = atomWithHash<string | undefined>('firstName', undefined, {
  setHash: 'replaceState',
});

export const lastNameAtom = atomWithHash<string | undefined>('lastName', undefined, {
  setHash: 'replaceState',
});

export const clinicianTypeAtom = atomWithHash<ClinicianTypeInput | undefined>(
  'clinicianType',
  undefined,
  {
    setHash: 'replaceState',
  }
);

export const countryAtom = atomWithHash<CountryType | undefined>(
  'country',
  getCountry() || undefined,
  {
    setHash: 'replaceState',
  }
);

export const otherCountryAtom = atomWithHash<string | undefined>(
  'otherCountry',
  undefined,
  {
    setHash: 'replaceState',
  }
);

export const jurisdictionAtom = atomWithHash<string | undefined>(
  'jurisdiction',
  undefined,
  {
    setHash: 'replaceState',
  }
);

export const otherClinicianTypeAtom = atomWithHash<string | undefined>(
  'otherClinicianType',
  undefined,
  {
    setHash: 'replaceState',
  }
);

export const phoneNumberAtom = atomWithHash<string | undefined>(
  'phoneNumber',
  undefined,
  {
    setHash: 'replaceState',
  }
);

export const demoSessionIdAtom = atomWithHash<string | undefined>(
  'demoSessionId',
  undefined,
  {
    setHash: 'replaceState',
  }
);

export const baaRequiredAtom = atomWithHash<boolean>('baaRequired', false, {
  setHash: 'replaceState',
});

export const complianceKitRequiredAtom = atomWithHash<boolean>(
  'complianceKitRequired',
  false,
  {
    setHash: 'replaceState',
  }
);

export const webinarOptInAtom = atomWithHash<boolean>('webinarOptIn', false, {
  setHash: 'replaceState',
});

export interface WebinarTimeslotValue {
  value: string;
  label: string;
  hubspotProperty: string;
}

export const webinarTimeslotAtom = atomWithHash<WebinarTimeslotValue | undefined>(
  'webinarTimeslot',
  undefined,
  {
    setHash: 'replaceState',
  }
);

// True when the user opted into a webinar but no sessions are available
// (booked out / fetch failed). Drives the phone-number fallback in
// `DemoOptInSection` and tells `useOnboardingCompletion` to submit the captured
// phone + reason via the SDR/request-demo path instead of a webinar signup.
export const webinarBookedOutAtom = atomWithHash<boolean>('webinarBookedOut', false, {
  setHash: 'replaceState',
});

export const signupSourceAtom = atomWithHash<SignupSource | undefined>(
  'signupSource',
  undefined,
  {
    setHash: 'replaceState',
  }
);

export const demoReasonAtom = atomWithHash<string | undefined>('demoReason', undefined, {
  setHash: 'replaceState',
});

export const displayLanguageAtom = atomWithHash<LanguageCode | undefined>(
  'displayLanguage',
  undefined,
  {
    setHash: 'replaceState',
  }
);
